import {
  Box,
  Chip,
  FormLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { getAPIErrorMessage } from '../../../helpers/helpers';
import { ImageService } from '../../../service/ImageService';
import { useAuthStore } from '../../../store/storeAuth';
import { useNotificationStore } from '../../../store/storeNotifications';
import {
  apiSystemToCanonical,
  canonicalImageSystemDisplayNameMap,
  openSourceModelsImage,
} from '../../AiConfigs/constants';
import KitModal from '../../UI/KitModal';
import { FormData } from '../constants';
import Fal from '../Fal';
import GeminiImage from '../GeminiImage';
import IdeogramAi from '../IdeogramAi';
import OpenAiImg from '../OpenAiImg';
import StabilityAi from '../StabilityAi';

type Props = {
  onHide: () => void;
  show: boolean;
  formData: FormData;
  onChange: (e: any) => void;
};

const SettingsModal = ({ onHide, show, formData, onChange }: Props) => {
  const setNotifications = useNotificationStore(state => state.setNotification);
  const { technologies } = useAuthStore(state => state);

  const imageSystems =
    formData.type === 'Text to Image'
      ? ['stabilityAi', 'openAi', 'replicate', 'ideogram', 'fal', 'gemini']
      : ['stabilityAi', 'ideogram'];

  const filteredImageSystems = technologies
    ?.filter(system => imageSystems.includes(system.system))
    .map(system => ({
      ...system,
      models: system?.models?.filter((model: any) => model.type === 'Image'),
    }));

  const [imageModels, setImageModels] = useState<
    {
      description: string;
      id: string;
      name: string;
      type: string;
    }[]
  >([]);
  useEffect(() => {
    const getImageLL = async () => {
      try {
        const response = await ImageService.getImage();

        setImageModels(response.data.message);
      } catch (error) {
        setNotifications({
          type: 'error',
          message: getAPIErrorMessage(error as any),
        });
      }
    };

    //getImageLL();
  }, []);

  return (
    <KitModal
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '680px',
          borderRadius: '16px',
        },
        '& .MuiDialogContent-root': {
          padding: '16px',
        },
        '& .MuiDialogActions-root': {
          padding: '16px',
        },
      }}
      showCloseButton={false}
      onHide={onHide}
      show={show}
    >
      <Box>
        <Typography variant='h6'>Advanced Settings</Typography>
        <Box mt={'16px'} p={'8px'} borderRadius={'4px'}>
          <Typography fontSize={'14px'} variant='h6'>
            Prompt Input
          </Typography>

          <Box>
            <FormLabel
              sx={{
                mb: '4px',
              }}
            >
              System
            </FormLabel>

            <Select
              className='nodrag'
              fullWidth
              value={formData.aiSystem}
              size='small'
              onChange={(e: any) => {
                onChange?.(e);
              }}
              name={'aiSystem'}
              sx={{
                width: '100%',
              }}
            >
              {filteredImageSystems?.map((opt, index) => {
                const disabled =
                  opt.models.length > 0
                    ? opt.models?.every((model: any) => model.disabledByCompany)
                    : false;
                const canonicalSystem =
                  apiSystemToCanonical[opt.system] ?? opt.system;
                return (
                  <MenuItem
                    key={index}
                    value={canonicalSystem}
                    disabled={disabled}
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    {canonicalImageSystemDisplayNameMap[
                      canonicalSystem as keyof typeof canonicalImageSystemDisplayNameMap
                    ] ?? canonicalSystem}
                    {disabled && (
                      <Chip
                        label='Disabled by admin'
                        color='error'
                        size='small'
                        variant='filled'
                        sx={{
                          borderRadius: '8px',
                        }}
                      />
                    )}
                  </MenuItem>
                );
              })}
            </Select>
          </Box>

          {formData.aiSystem === 'stabilityai' && (
            <StabilityAi
              onChange={onChange}
              formData={formData}
              imageModels={imageModels}
            />
          )}

          {formData.aiSystem === 'opensources' && (
            <Box>
              <FormLabel htmlFor='aiModel'>Models </FormLabel>
              <Select
                name='aiModel'
                className='nodrag'
                fullWidth
                value={formData.aiModel}
                onChange={onChange}
              >
                {openSourceModelsImage?.map((imageModel, index) => (
                  <MenuItem key={index} value={imageModel.value}>
                    {imageModel.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}
          {formData.aiSystem === 'openai' && (
            <OpenAiImg onChange={onChange} formData={formData} />
          )}
          {formData.aiSystem === 'ideogram' && (
            <IdeogramAi onChange={onChange} formData={formData} />
          )}
          {formData.aiSystem === 'fal' && (
            <Fal onChange={onChange} formData={formData} />
          )}
          {formData.aiSystem === 'gemini' && (
            <GeminiImage onChange={onChange} formData={formData} />
          )}
        </Box>
      </Box>
    </KitModal>
  );
};

export default SettingsModal;
